import * as React from 'react';
import {
  Search,
  SlidersHorizontal,
  X,
  ArrowUpDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils/cn';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type SortOption =
  | 'reciente'
  | 'precio_asc'
  | 'precio_desc'
  | 'popular'
  | 'cercano';

type Condition = 'nuevo' | 'como_nuevo' | 'buen_estado' | 'usado';

interface Filters {
  query: string;
  priceMin?: number;
  priceMax?: number;
  conditions: Condition[];
  sameBuildingOnly: boolean;
  sort: SortOption;
}

interface SearchFilterBarProps {
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
  /** Total results count for display */
  resultCount?: number;
  className?: string;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'reciente', label: 'Mas reciente' },
  { value: 'precio_asc', label: 'Menor precio' },
  { value: 'precio_desc', label: 'Mayor precio' },
  { value: 'popular', label: 'Mas popular' },
  { value: 'cercano', label: 'Mas cercano' },
];

const CONDITION_OPTIONS: { value: Condition; label: string }[] = [
  { value: 'nuevo', label: 'Nuevo' },
  { value: 'como_nuevo', label: 'Como nuevo' },
  { value: 'buen_estado', label: 'Buen estado' },
  { value: 'usado', label: 'Usado' },
];

const PRICE_RANGES: { label: string; min?: number; max?: number }[] = [
  { label: 'Cualquier precio' },
  { label: 'Hasta $10.000', max: 10000 },
  { label: '$10.000 - $50.000', min: 10000, max: 50000 },
  { label: '$50.000 - $150.000', min: 50000, max: 150000 },
  { label: '$150.000 - $500.000', min: 150000, max: 500000 },
  { label: 'Mas de $500.000', min: 500000 },
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

/**
 * SearchFilterBar
 * ---------------
 * Combined search input + filter controls for the marketplace.
 *
 * Layout (desktop):
 * ┌──────────────────────────────────────────────────────────┐
 * │  🔍 [Search input...................] [Filtros] [Ordenar] │
 * ├──────────────────────────────────────────────────────────┤
 * │  [Active filter chips...]        N resultados            │
 * └──────────────────────────────────────────────────────────┘
 *
 * Layout (mobile):
 * - Search input goes full width
 * - Filter/sort buttons become icon-only
 * - Expanded filter panel slides down
 *
 * Visual treatment:
 * - Search input uses paper-dim bg (like topbar search)
 * - Focus: border-primary with primary ring
 * - Filter panel: paper background, hairline border top
 * - Active filters shown as removable badge chips
 * - Result count in eyebrow style
 */
export function SearchFilterBar({
  filters,
  onFiltersChange,
  resultCount,
  className,
}: SearchFilterBarProps) {
  const [isFilterOpen, setIsFilterOpen] = React.useState(false);

  // Helper to update a single filter key
  function update(partial: Partial<Filters>) {
    onFiltersChange({ ...filters, ...partial });
  }

  // Count active non-default filters
  const activeFilterCount =
    (filters.conditions.length > 0 ? 1 : 0) +
    (filters.priceMin !== undefined || filters.priceMax !== undefined ? 1 : 0) +
    (filters.sameBuildingOnly ? 1 : 0);

  // Which price range label is active
  const activePriceRange = PRICE_RANGES.find(
    (r) => r.min === filters.priceMin && r.max === filters.priceMax,
  );

  function toggleCondition(cond: Condition) {
    const next = filters.conditions.includes(cond)
      ? filters.conditions.filter((c) => c !== cond)
      : [...filters.conditions, cond];
    update({ conditions: next });
  }

  function clearFilters() {
    update({
      conditions: [],
      priceMin: undefined,
      priceMax: undefined,
      sameBuildingOnly: false,
    });
  }

  return (
    <div className={cn('space-y-0', className)}>
      {/* ---- Main bar ---- */}
      <div className="flex items-center gap-2">
        {/* Search input */}
        <div className="group relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-ink-faint)] transition-colors group-focus-within:text-[var(--color-primary)]" />
          <input
            type="search"
            value={filters.query}
            onChange={(e) => update({ query: e.target.value })}
            placeholder="Buscar en el mercado vecinal..."
            aria-label="Buscar productos"
            className={cn(
              'h-10 w-full rounded-md border border-[var(--color-hairline-strong)] bg-[var(--color-paper-dim)] pl-10 pr-3 text-sm text-[var(--color-ink)]',
              'placeholder:text-[var(--color-ink-faint)] transition-all duration-150',
              'focus:border-[var(--color-primary)] focus:bg-[var(--color-paper)] focus:outline-none',
              'focus:shadow-[0_0_0_3px_color-mix(in_oklch,var(--color-primary)_20%,transparent)]',
            )}
          />
          {filters.query && (
            <button
              type="button"
              aria-label="Limpiar busqueda"
              onClick={() => update({ query: '' })}
              className="absolute right-2 top-1/2 -translate-y-1/2 grid h-5 w-5 place-items-center rounded text-[var(--color-ink-faint)] transition-colors hover:text-[var(--color-ink)]"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Filter toggle */}
        <Button
          type="button"
          variant={isFilterOpen ? 'primary' : 'outline'}
          size="md"
          onClick={() => setIsFilterOpen(!isFilterOpen)}
          className="relative shrink-0"
          aria-expanded={isFilterOpen}
          aria-controls="marketplace-filters"
        >
          <SlidersHorizontal className="h-4 w-4" />
          <span className="hidden sm:inline">Filtros</span>
          {activeFilterCount > 0 && (
            <span className="absolute -right-1 -top-1 grid h-4 w-4 place-items-center rounded-full bg-[var(--color-accent)] text-[0.5625rem] font-bold text-[var(--color-paper)]">
              {activeFilterCount}
            </span>
          )}
        </Button>

        {/* Sort select */}
        <Select
          value={filters.sort}
          onValueChange={(v) => update({ sort: v as SortOption })}
        >
          <SelectTrigger className="w-auto shrink-0 gap-1.5">
            <ArrowUpDown className="h-3.5 w-3.5 text-[var(--color-ink-faint)]" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* ---- Expanded filter panel ---- */}
      {isFilterOpen && (
        <div
          id="marketplace-filters"
          className="stagger-fade mt-3 rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] p-4"
        >
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
            {/* Price range */}
            <div>
              <p className="eyebrow mb-2">Rango de precio</p>
              <div className="space-y-1">
                {PRICE_RANGES.map((range) => {
                  const isActive =
                    range.min === filters.priceMin &&
                    range.max === filters.priceMax;
                  return (
                    <button
                      key={range.label}
                      type="button"
                      onClick={() =>
                        update({ priceMin: range.min, priceMax: range.max })
                      }
                      className={cn(
                        'flex w-full items-center rounded px-2.5 py-1.5 text-left text-[0.8125rem] transition-colors',
                        isActive
                          ? 'bg-[var(--color-primary-50)] font-medium text-[var(--color-primary)]'
                          : 'text-[var(--color-ink-soft)] hover:bg-[var(--color-paper-dim)]',
                      )}
                    >
                      {range.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Condition */}
            <div>
              <p className="eyebrow mb-2">Condicion</p>
              <div className="space-y-1">
                {CONDITION_OPTIONS.map((cond) => {
                  const isActive = filters.conditions.includes(cond.value);
                  return (
                    <button
                      key={cond.value}
                      type="button"
                      role="checkbox"
                      aria-checked={isActive}
                      onClick={() => toggleCondition(cond.value)}
                      className={cn(
                        'flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-left text-[0.8125rem] transition-colors',
                        isActive
                          ? 'bg-[var(--color-primary-50)] font-medium text-[var(--color-primary)]'
                          : 'text-[var(--color-ink-soft)] hover:bg-[var(--color-paper-dim)]',
                      )}
                    >
                      {/* Custom checkbox indicator */}
                      <span
                        className={cn(
                          'grid h-4 w-4 shrink-0 place-items-center rounded-sm border transition-colors',
                          isActive
                            ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-[var(--color-paper)]'
                            : 'border-[var(--color-hairline-strong)]',
                        )}
                      >
                        {isActive && (
                          <svg
                            viewBox="0 0 12 12"
                            className="h-2.5 w-2.5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <polyline points="2,6 5,9 10,3" />
                          </svg>
                        )}
                      </span>
                      {cond.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Proximity */}
            <div>
              <p className="eyebrow mb-2">Proximidad</p>
              <button
                type="button"
                role="checkbox"
                aria-checked={filters.sameBuildingOnly}
                onClick={() =>
                  update({ sameBuildingOnly: !filters.sameBuildingOnly })
                }
                className={cn(
                  'flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-left text-[0.8125rem] transition-colors',
                  filters.sameBuildingOnly
                    ? 'bg-[var(--color-primary-50)] font-medium text-[var(--color-primary)]'
                    : 'text-[var(--color-ink-soft)] hover:bg-[var(--color-paper-dim)]',
                )}
              >
                <span
                  className={cn(
                    'grid h-4 w-4 shrink-0 place-items-center rounded-sm border transition-colors',
                    filters.sameBuildingOnly
                      ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-[var(--color-paper)]'
                      : 'border-[var(--color-hairline-strong)]',
                  )}
                >
                  {filters.sameBuildingOnly && (
                    <svg
                      viewBox="0 0 12 12"
                      className="h-2.5 w-2.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="2,6 5,9 10,3" />
                    </svg>
                  )}
                </span>
                Solo mi edificio
              </button>
            </div>
          </div>

          {/* Clear all button */}
          {activeFilterCount > 0 && (
            <div className="mt-4 border-t border-[var(--color-hairline)] pt-3">
              <button
                type="button"
                onClick={clearFilters}
                className="text-[0.8125rem] font-medium text-[var(--color-primary)] transition-colors hover:text-[var(--color-primary-600)]"
              >
                Limpiar filtros
              </button>
            </div>
          )}
        </div>
      )}

      {/* ---- Active filter chips + result count ---- */}
      {(activeFilterCount > 0 || resultCount !== undefined) && (
        <div className="mt-2.5 flex items-center gap-2 overflow-x-auto">
          {/* Active filter badges */}
          {filters.conditions.map((cond) => (
            <Badge
              key={cond}
              variant="primary"
              className="cursor-pointer gap-1 hover:opacity-80"
              onClick={() => toggleCondition(cond)}
            >
              {CONDITION_OPTIONS.find((c) => c.value === cond)?.label}
              <X className="h-2.5 w-2.5" />
            </Badge>
          ))}
          {activePriceRange && activePriceRange.label !== 'Cualquier precio' && (
            <Badge
              variant="primary"
              className="cursor-pointer gap-1 hover:opacity-80"
              onClick={() =>
                update({ priceMin: undefined, priceMax: undefined })
              }
            >
              {activePriceRange.label}
              <X className="h-2.5 w-2.5" />
            </Badge>
          )}
          {filters.sameBuildingOnly && (
            <Badge
              variant="primary"
              className="cursor-pointer gap-1 hover:opacity-80"
              onClick={() => update({ sameBuildingOnly: false })}
            >
              Solo mi edificio
              <X className="h-2.5 w-2.5" />
            </Badge>
          )}

          {/* Result count — pushed right */}
          {resultCount !== undefined && (
            <span className="ml-auto shrink-0 text-[0.6875rem] text-[var(--color-ink-faint)]">
              {resultCount} resultado{resultCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
